import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Calendar, Globe, Check } from 'lucide-react';
import { useContractorAuth } from '../../contexts/ContractorAuthContext';

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese'
};

export const ContractorDocuments = () => {
  const { api } = useContractorAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/contractors/my-documents');
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif text-white">My Documents</h1>
        <p className="text-gray-400 text-sm mt-1">View your signed agreements and documents</p>
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-8 text-center">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No documents found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 border border-white/10 p-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#C6A87C]/20 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-[#C6A87C]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Independent Contractor Agreement</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Signed: {new Date(doc.signed_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {LANGUAGE_LABELS[doc.language] || doc.language}
                      </span>
                      <span className="flex items-center gap-1 text-green-400">
                        <Check className="w-3 h-3" />
                        {doc.status}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-2">
                      Signed as: <span className="italic">{doc.electronic_signature}</span>
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 sm:flex-col">
                  <div className="text-xs text-gray-500 bg-white/5 px-2 py-1 text-center">
                    English Copy
                  </div>
                  {doc.language !== 'en' && (
                    <div className="text-xs text-gray-500 bg-white/5 px-2 py-1 text-center">
                      {LANGUAGE_LABELS[doc.language]} Copy
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 p-4">
        <p className="text-blue-300 text-sm">
          All signed agreements are stored securely. Copies are available in English and your selected language.
          Contact your supervisor if you need additional copies.
        </p>
      </div>
    </div>
  );
};

export default ContractorDocuments;
